import { Pets as PawIcon } from "@mui/icons-material";
import {
  Avatar, Box, Card, CardContent, Container, Grid, Typography,
  useTheme, useMediaQuery, IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { reviews as fallbackReviews } from "../data/reviews.ts";
import { supabase } from "../supabaseClient.ts";

type Review = {
  id?: string;
  name: string;
  dog_names?: string | null;
  message: string;
  date?: string;
};

const Section = styled(Box)<{ $dark:boolean }>(({theme,$dark})=>({
  padding:theme.spacing(8,0),
  backgroundColor:$dark?theme.palette.background.default:"#f8f4f7"
}));
const ReviewCard = styled(Card)(({theme})=>({
  height:"100%", borderRadius:theme.spacing(1.5), boxShadow:theme.shadows[3]
}));

export const ReviewsSection:React.FC=()=>{
  const theme=useTheme();
  const mobile=useMediaQuery(theme.breakpoints.down("md"));
  const [items,setItems]=useState<Review[]>(fallbackReviews);
  const [expanded,setExpanded]=useState<Set<number>>(new Set());

  useEffect(()=>{
    if(!supabase)return;
    supabase.rpc("public_list_reviews_v1").then(({data,error})=>{
      if(!error && Array.isArray(data) && data.length){
        setItems(data as Review[]);
      }
    });
  },[]);

  const format=(v?:string)=>v?new Date(`${v}T12:00:00`).toLocaleDateString("nl-NL",{
    day:"numeric",month:"long",year:"numeric"
  }):"";

  return <Section id="reviews" component="section" $dark={theme.palette.mode==="dark"}>
    <Container maxWidth="lg">
      <Box sx={{textAlign:"center",mb:6}}>
        <Typography variant="h4" component="h2" color="primary.main"
          sx={{display:"flex",justifyContent:"center",alignItems:"center",gap:1,fontWeight:600}}>
          <PawIcon/> Klantbeoordelingen
        </Typography>
        <Typography color="text.secondary" sx={{mt:1.5}}>
          Ervaringen van baasjes van honden die bij Pippaya hebben verbleven
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {items.slice(0,6).map((r,i)=>{
          const open=expanded.has(i);
          const long=r.message.length>200;
          return <Grid item xs={12} md={6} key={r.id??`${r.name}-${i}`}>
            <ReviewCard>
              <CardContent sx={{p:3,height:"100%",display:"flex",flexDirection:"column"}}>
                <Box sx={{display:"flex",alignItems:"center",mb:2}}>
                  <Avatar sx={{bgcolor:"primary.main",mr:1.5}}><PawIcon/></Avatar>
                  <Box>
                    <Typography color="primary.main" fontWeight={600}>{r.name}</Typography>
                    {r.dog_names&&<Typography variant="caption" color="text.secondary">
                      over {r.dog_names}
                    </Typography>}
                  </Box>
                </Box>
                <Typography color="text.secondary" sx={{
                  fontStyle:"italic",lineHeight:1.65,
                  ...(!open?{
                    display:"-webkit-box",WebkitLineClamp:3,
                    WebkitBoxOrient:"vertical",overflow:"hidden"
                  }:{})
                }}>
                  "{r.message}"
                </Typography>
                <Box sx={{mt:"auto",pt:2,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <Typography variant="caption" color="text.disabled">{format(r.date)}</Typography>
                  {long&&<IconButton size="small" color="primary" onClick={()=>{
                    setExpanded(prev=>{
                      const n=new Set(prev); n.has(i)?n.delete(i):n.add(i); return n;
                    });
                  }}>{open?<ExpandLessIcon/>:<ExpandMoreIcon/>}</IconButton>}
                </Box>
              </CardContent>
            </ReviewCard>
          </Grid>;
        })}
      </Grid>
    </Container>
  </Section>;
};
